import {
  ArrowIcon,
  Badge,
  colors,
  CompanyShopIcon,
  IconButton,
  MarketingShopIcon,
  PopupShopIcon,
  ShopifyShopIcon,
  PoDShopIcon,
  Text,
} from "@commonsku/styles";
import React from "react";
import { useDispatch } from "react-redux";
import { createCopyOrderPopup } from "../../../actions/popup";
import { useIdentity } from "../../../hooks";
import { getIdentityUtils } from "../../../utils";
import { trackEvent } from "../../../utils/analytics";

interface InitializationProps {
  handleCustomRouting: (newRoute: string, selectedShopType?: string) => void;
  create_shop: any;
  setShopPopup: (value: boolean) => void;
  isShopsTermsAndConditionsAccepted?: boolean;
}

const Initialization = ({
  handleCustomRouting,
  create_shop,
  setShopPopup,
  isShopsTermsAndConditionsAccepted,
}: InitializationProps) => {
  const dispatch = useDispatch();
  const identity = useIdentity();
  const { hasCapabilities, hasUserFlags } = getIdentityUtils(identity);
  const hasShopifyIntegration = hasCapabilities(["FEATURE-ADVANCED", "FEATURE-ENTERPRISE"], true);
  const hasPoDEnabled = hasUserFlags("POD_ENABLED");
  const isCopyFromOrder = !!create_shop?.order_id;

  const handleCreateShop = (shopType: string) => {
    if (shopType === "POD" && isCopyFromOrder) {
      return;
    }

    if (shopType === "POD") {
      trackEvent("PoD - Select PoD Shop", {
        tenant_id: identity.tenant_id,
        login_email: identity.login_email,
      });
    }

    if (
      create_shop === true ||
      create_shop === "no_redirect" ||
      (typeof create_shop === "object" && create_shop?.clientId)
    ) {
      if (
        shopType === "SHOPIFY" &&
        isShopsTermsAndConditionsAccepted === false
      ) {
        handleCustomRouting("initialize", shopType);
      } else {
        handleCustomRouting("selectedShopPage", shopType);
      }
    } else {
      setShopPopup(false);
      dispatch(
        createCopyOrderPopup(create_shop, "SHOP", shopType.toLowerCase())
      );
    }
  };

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
        textAlign: "left",
        height: "100%",
      }}
    >
      <div>
        <Text
          as="h1"
          style={{
            fontSize: 56,
            fontWeight: 400,
            color: colors.neutrals["90"],
            width: "70%",
            marginBottom: 64,
          }}
        >
          Answer a few questions to find your ideal Shop type
        </Text>
        <IconButton
          variant="primary"
          size="huge"
          Icon={ArrowIcon}
          iconPosition="right"
          onClick={() => handleCustomRouting("questionnaire")}
          id="shopCreation-initialPage-getStarted.btn"
        >
          Get Started
        </IconButton>
      </div>
      <div id="shopCreation-initialPage-getStarted.shopIconsWrapperDiv">
        <Text
          as="h2"
          style={{
            fontSize: 24,
            fontWeight: 600,
            marginBottom: 48,
            color: colors.neutrals["90"],
          }}
        >
          Or select your Shop type below
        </Text>
        <div style={{ display: "flex" }}>
          {hasPoDEnabled && !isCopyFromOrder ? (
            <IconButton
              variant="text"
              size="huge"
              Icon={PoDShopIcon({
                color: colors.primary1["70"],
                width: 80,
                height: 80,
              })}
              iconPosition="top"
              style={{
                position: "relative",
                marginRight: 48,
                borderRadius: 16,
                color: colors.neutrals["90"],
                width: 208,
                height: 160,
              }}
              onClick={() => handleCreateShop("POD")}
              id="shopCreation-initialPage-getStarted-podShop.btn"
            >
              <Badge
                style={{
                  position: "absolute",
                  top: 8,
                  right: 8,
                  padding: "2px 5px",
                  borderRadius: 100,
                  backgroundColor: colors.secondary1.main,
                }}
              >
                <Text style={{
                  width: "21px",
                  height: "16px",
                  fontSize: "10px",
                  fontWeight: 450,
                  lineHeight: "16px",
                  verticalAlign: "middle",
                  whiteSpace: "nowrap",
                  paddingTop: "1px",
                  color: colors.neutrals.white,
                }}>
                  New
                </Text>
              </Badge>
              Print on Demand<br />Shop
            </IconButton>
          ) : null}
          <IconButton
            variant="text"
            size="huge"
            Icon={PopupShopIcon({ color: "#E6BA00", width: 80, height: 80 })}
            iconPosition="top"
            style={{
              marginRight: 48,
              borderRadius: 16,
              color: colors.neutrals["90"],
              width: 208,
              height: 160,
            }}
            onClick={() => handleCreateShop("POPUP")}
            id="shopCreation-initialPage-getStarted-popupShop.btn"
          >
            Pop-up Shop
          </IconButton>
          <IconButton
            variant="text"
            size="huge"
            Icon={CompanyShopIcon({
              color: colors.secondary3["60"],
              width: 80,
              height: 80,
            })}
            iconPosition="top"
            style={{
              marginRight: 48,
              borderRadius: 16,
              color: colors.neutrals["90"],
              width: 208,
              height: 160,
            }}
            onClick={() => handleCreateShop("COMPANY")}
            id="shopCreation-initialPage-getStarted-companyShop.btn"
          >
            Company Shop
          </IconButton>
          <IconButton
            variant="text"
            size="huge"
            Icon={MarketingShopIcon({
              color: colors.primary1["70"],
              width: 80,
              height: 80,
            })}
            iconPosition="top"
            style={{
              marginRight: 48,
              borderRadius: 16,
              color: colors.neutrals["90"],
              width: 208,
              height: 160,
            }}
            onClick={() => handleCreateShop("MARKETING")}
            id="shopCreation-initialPage-getStarted-marketingShop.btn"
          >
            Marketing Shop
          </IconButton>
          {hasShopifyIntegration ? (
            <IconButton
              variant="text"
              size="huge"
              Icon={ShopifyShopIcon({
                color: "#95BF47",
                width: 80,
                height: 80,
              })}
              iconPosition="top"
              style={{
                marginRight: 48,
                borderRadius: 16,
                color: colors.neutrals["90"],
                width: 208,
                height: 160,
              }}
              onClick={() => handleCreateShop("SHOPIFY")}
              id="shopCreation-initialPage-getStarted-shopifyShop.btn"
            >
              Shopify Connected Shop
            </IconButton>
          ) : null}
        </div>
      </div>
    </div>
  );
};

export default Initialization;
